/**
 * Billing Service
 *
 * API service for subscription and billing operations.
 * Handles subscription status, checkout, billing portal, and billing history.
 */

import { apiClient } from "./api";
import type {
  ActionResponse,
  BillingHistoryResponse,
  CheckoutRequest,
  CheckoutResponse,
  PortalRequest,
  PortalResponse,
  SubscriptionPlan,
  SubscriptionStatusResponse,
  UserSubscription,
  ValidateDiscountRequest,
  ValidateDiscountResponse,
} from "../types/billing";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get the current user's subscription status.
 * This is a lightweight check for feature access.
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const response = await apiClient.get<SubscriptionStatusResponse>(
    `${API_PREFIX}/billing/subscription`
  );
  return response.data;
}

/**
 * Get detailed subscription information.
 */
export async function getSubscriptionDetails(): Promise<UserSubscription> {
  const response = await apiClient.get<UserSubscription>(
    `${API_PREFIX}/billing/subscription/details`
  );
  return response.data;
}

/**
 * Get available subscription plans.
 */
export async function getAvailablePlans(): Promise<SubscriptionPlan[]> {
  const response = await apiClient.get<SubscriptionPlan[]>(
    `${API_PREFIX}/billing/plans`
  );
  return response.data;
}

/**
 * Get all public plans including free plans.
 * Used on the marketing homepage to display pricing.
 * No authentication required.
 */
export async function getPublicPlans(): Promise<SubscriptionPlan[]> {
  const response = await apiClient.get<SubscriptionPlan[]>(
    `${API_PREFIX}/billing/plans`,
    { params: { include_free: true } }
  );
  return response.data;
}

/**
 * Create a Stripe Checkout session for a new subscription.
 *
 * @param request - Checkout request with plan ID and redirect URLs
 * @returns Checkout response with redirect URL
 */
export async function createCheckoutSession(
  request: CheckoutRequest
): Promise<CheckoutResponse> {
  const response = await apiClient.post<CheckoutResponse>(
    `${API_PREFIX}/billing/checkout`,
    request
  );
  return response.data;
}

/**
 * Create a Stripe Customer Portal session.
 * Allows users to manage their subscription, update payment methods, etc.
 *
 * @param request - Portal request with return URL
 * @returns Portal response with redirect URL
 */
export async function createPortalSession(
  request: PortalRequest
): Promise<PortalResponse> {
  const response = await apiClient.post<PortalResponse>(
    `${API_PREFIX}/billing/portal`,
    request
  );
  return response.data;
}

/**
 * Retry a failed payment.
 */
export async function retryPayment(): Promise<ActionResponse> {
  const response = await apiClient.post<ActionResponse>(
    `${API_PREFIX}/billing/retry-payment`
  );
  return response.data;
}

/**
 * Cancel the current subscription.
 *
 * @param atPeriodEnd - If true, cancel at end of billing period. If false, cancel immediately.
 */
export async function cancelSubscription(
  atPeriodEnd: boolean = true
): Promise<ActionResponse> {
  const response = await apiClient.post<ActionResponse>(
    `${API_PREFIX}/billing/cancel`,
    null,
    {
      params: { at_period_end: atPeriodEnd },
    }
  );
  return response.data;
}

/**
 * Reactivate a subscription that was set to cancel at period end.
 */
export async function reactivateSubscription(): Promise<ActionResponse> {
  const response = await apiClient.post<ActionResponse>(
    `${API_PREFIX}/billing/reactivate`
  );
  return response.data;
}

/**
 * Get billing history (events) for the current user.
 *
 * @param limit - Maximum number of events to return
 */
export async function getBillingHistory(
  limit: number = 50
): Promise<BillingHistoryResponse> {
  const response = await apiClient.get<BillingHistoryResponse>(
    `${API_PREFIX}/billing/history`,
    {
      params: { limit },
    }
  );
  return response.data;
}

/**
 * Validate a discount code for a specific plan.
 *
 * @param request - Validation request with code and plan ID
 * @returns Validation response with discount details
 */
export async function validateDiscountCode(
  request: ValidateDiscountRequest
): Promise<ValidateDiscountResponse> {
  const response = await apiClient.post<ValidateDiscountResponse>(
    `${API_PREFIX}/billing/validate-discount`,
    request
  );
  return response.data;
}
